/**
 * Cities & Communities building type configuration.
 * Extracted from citiesCommunities/ vertical.
 *
 * Uses different input vocabulary (walkScore, treeCanopy%, skyViewFactor)
 * but maps to the same 8 pathways via its own model/pathways.
 */

// Urban-specific model (different from building-scale)
import { assessZone, assessDistrict, compareScenarios } from '../../citiesCommunities/engine/model';
import * as urbanPathways from '../../citiesCommunities/engine/pathways';
import * as urbanParameters from '../../citiesCommunities/engine/parameters';

// Points system (UBHPS)
import { ubhpsPathwayCaps, ubhpsPreconditions, ubhpsFeatures } from './urbanPointsAdapter';

// Financial engine
import { computeCommunityImpact } from '../../citiesCommunities/engine/financialImpact';

// Neuroscience
import * as urbanNeuroscience from '../../citiesCommunities/engine/neuroscience';

export default {
  type: 'urban',
  label: 'Cities & Communities',
  shortLabel: 'Cities',
  color: '#0D9488',  // Teal
  icon: 'city',

  // --- Unit of analysis ---
  unitLabel: 'Zone',
  unitLabelPlural: 'Zones',
  unitIdField: 'zoneId',

  // --- Zone types ---
  spaceTypes: [
    { id: 'district', label: 'District', icon: 'map' },
    { id: 'corridor', label: 'Street Corridor', icon: 'road' },
    { id: 'neighborhood', label: 'Neighborhood', icon: 'home' },
    { id: 'campus', label: 'Campus', icon: 'university' },
    { id: 'park', label: 'Park / Green Space', icon: 'tree' },
    { id: 'transit_area', label: 'Transit Area', icon: 'bus' },
    { id: 'commercial', label: 'Commercial Zone', icon: 'store' },
    { id: 'mixed_use', label: 'Mixed-Use Zone', icon: 'building' },
  ],

  // --- Input fields (urban-scale indicators) ---
  inputFields: {
    walkScore: { label: 'Walk Score (0-100)', unit: '', pathway: 'movement' },
    bikeInfrastructure: { label: 'Bike Infrastructure (0-1)', unit: '', pathway: 'movement' },
    blockPermeability: { label: 'Block Permeability (0-1)', unit: '', pathway: 'movement' },
    treeCanopyPct: { label: 'Tree Canopy (%)', unit: '%', pathway: 'nature' },
    greenSpaceAccess300m: { label: 'Green Space Access 300m (%)', unit: '%', pathway: 'nature' },
    permeableSurfacePct: { label: 'Permeable Surface (%)', unit: '%', pathway: 'nature' },
    skyViewFactor: { label: 'Sky View Factor (0-1)', unit: '', pathway: 'daylight' },
    sunlightHoursGroundPlane: { label: 'Sunlight Hours', unit: 'hr', pathway: 'daylight' },
    pm25Annual: { label: 'PM2.5 Annual (µg/m³)', unit: 'µg/m³', pathway: 'air_quality' },
    pctZoneOver100mFromTraffic: { label: '% Zone >100m from Traffic', unit: '%', pathway: 'air_quality' },
    greenBufferScore: { label: 'Green Buffer Score (0-1)', unit: '', pathway: 'air_quality' },
    pctZoneOver65dB: { label: '% Zone >65 dB', unit: '%', pathway: 'noise' },
    pctQuietAreas: { label: '% Quiet Areas', unit: '%', pathway: 'noise' },
    soundEnvironmentQuality: { label: 'Sound Environment (0-1)', unit: '', pathway: 'noise' },
    mixedUseIndex: { label: 'Mixed-Use Index (0-1)', unit: '', pathway: 'enrichment' },
    groundPlaneActivationPct: { label: 'Ground Activation (%)', unit: '%', pathway: 'enrichment' },
    socialInfraPerThousand: { label: 'Social Infra per 1000', unit: '', pathway: 'enrichment' },
    streetIntelligibility: { label: 'Street Intelligibility (0-1)', unit: '', pathway: 'wayfinding' },
    landmarkScore: { label: 'Landmark Score (0-1)', unit: '', pathway: 'wayfinding' },
    facadeDiversityIndex: { label: 'Facade Diversity (0-1)', unit: '', pathway: 'visual' },
    streetscapeVisualInterest: { label: 'Streetscape Interest (0-1)', unit: '', pathway: 'visual' },
  },

  // --- Custom model (urban-scale, not building-scale) ---
  customModel: {
    assessSpace: assessZone,   // zone = urban "space"
    assessJourney: assessDistrict,  // district = urban "journey"
    compareScenarios,
    pathways: urbanPathways,
    parameters: urbanParameters,
  },

  // --- Optimizer targets (urban-scale) ---
  optimizerTargets: {
    movement: {
      field: 'walkScore', target: 85, unit: 'score',
      rationale: 'Walk Score >70 associated with 32% more walking trips (Frank et al. 2010)',
      action: 'Improve walkability',
      details: 'Add pedestrian crossings, widen sidewalks, improve block permeability',
      mechanism: 'Walking increases BDNF via PGC-1α/FNDC5/irisin pathway (Wrann 2013)',
      category: 'major_investment',
    },
    nature: {
      field: 'treeCanopyPct', target: 30, unit: '%',
      rationale: 'WHO recommends ≥30% tree canopy for urban health (Kardan 2015)',
      action: 'Increase tree canopy coverage',
      details: 'Plant street trees, create pocket parks, add green corridors between zones',
      mechanism: 'Nature exposure reduces cortisol by 9.6%/hour (Hunter 2019)',
      category: 'major_investment',
    },
    air_quality: {
      field: 'pm25Annual', target: 10, unit: 'µg/m³',
      rationale: 'WHO annual guideline; below 12 µg/m³ precondition (Liu 2021)',
      action: 'Reduce ambient PM2.5',
      details: 'Add green buffers along traffic corridors, expand low-emission zones',
      mechanism: 'PM2.5 causes neuroinflammation; HR 1.15 per µg/m³ for AD risk (Liu 2021)',
      category: 'major_investment',
    },
    noise: {
      field: 'pctZoneOver65dB', target: 10, unit: '%',
      rationale: 'Minimize area exposed to traffic noise >65 dB (WHO guidelines)',
      action: 'Reduce noise exposure zones',
      details: 'Add sound barriers, increase quiet areas, create noise buffer zones with vegetation',
      mechanism: 'Chronic noise >65 dB inhibits hippocampal LTP (de Deus 2017)',
      category: 'moderate',
    },
    daylight: {
      field: 'skyViewFactor', target: 0.5, unit: 'ratio',
      rationale: 'Sky view factor >0.4 ensures adequate daylight at street level',
      action: 'Improve sky view and daylight access',
      details: 'Reduce canyon effect, step back upper floors, orient streets for solar access',
      mechanism: 'Daylight activates ipRGC photoreceptors, driving circadian CREB signaling (Brown 2022)',
      category: 'major_investment',
    },
    enrichment: {
      field: 'mixedUseIndex', target: 0.7, unit: 'index',
      rationale: 'Mixed-use districts provide cognitive stimulation and social affordances',
      action: 'Increase land-use diversity',
      details: 'Add ground-floor retail, social infrastructure, cultural amenities',
      mechanism: 'Enrichment drives BDNF via cognitive challenge (r=0.62, Vega-Rosas 2024)',
      category: 'moderate',
    },
    wayfinding: {
      field: 'streetIntelligibility', target: 0.7, unit: 'score',
      rationale: 'Intelligible street networks engage hippocampal spatial navigation (Banner 2011)',
      action: 'Improve street legibility',
      details: 'Add landmark features, clear signage hierarchy, improve sightlines',
      mechanism: 'Good wayfinding engages hippocampal place cells (Banner 2011)',
      category: 'moderate',
    },
    visual_stress: {
      field: 'facadeDiversityIndex', target: 0.6, unit: 'index',
      rationale: 'Diverse facades reduce visual monotony and pattern stress (Valentine 2025)',
      action: 'Increase facade diversity',
      details: 'Vary materials, heights, setbacks; add architectural variety to street frontage',
      mechanism: 'Repetitive patterns at ~3 cpd trigger cortical hyperactivation (Valentine 2025)',
      category: 'moderate',
    },
  },

  // --- Points system ---
  points: {
    name: 'Brain Health',
    fullName: 'Brain Health Points',
    // Points functions are loaded from the citiesCommunities engine
    // and adapted via urbanPointsAdapter
    pathwayCaps: urbanParameters.PATHWAY_WEIGHTS,  // Placeholder; actual caps in ubhps.js
    tiers: { platinum: 75, gold: 60, silver: 45, bronze: 30 },
    // These will be loaded dynamically from citiesCommunities/engine/ubhps
  },

  // --- Financial model (community impact) ---
  financial: {
    staffLabel: null,  // No staff model for cities
    occupantLabel: 'Community',
    calculateCommunityImpact: computeCommunityImpact,
    channels: [
      { name: 'Health Outcomes', key: 'health', color: '#EF4444' },
      { name: 'Economic Activation', key: 'economic', color: '#F59E0B' },
      { name: 'Quality of Life', key: 'quality', color: '#8B5CF6' },
      { name: 'Climate Resilience', key: 'climate', color: '#10B981' },
    ],
  },

  // --- Neuroscience ---
  neuroscience: {
    outcomeLabels: {
      dorsal: 'Cognitive & Spatial (Dorsal Hippocampus)',
      ventral: 'Emotional & Mood (Ventral Hippocampus)',
    },
    contextDescription: 'Urban environments impact community brain health through walkability, green infrastructure, and environmental quality.',
    activatingWeights: { movement: 0.30, enrichment: 0.25, nature: 0.25, daylight: 0.20 },
    inhibitingWeights: { air_quality: 0.35, noise: 0.35, visual: 0.30 },
    computeActivating: urbanNeuroscience.computeActivating,
    computeInhibiting: urbanNeuroscience.computeInhibiting,
    computeSynergy: urbanNeuroscience.computeSynergy,
    computeBdnfBreakdown: urbanNeuroscience.computeBdnfBreakdown,
    computeTemporalOutcomes: urbanNeuroscience.computeTemporalOutcomes,
    classifyNetEffect: urbanNeuroscience.classifyNetEffect,
    netEffectDescription: urbanNeuroscience.netEffectDescription,
    temporalLabels: {
      immediate: 'Immediate (hours-days)',
      shortTerm: 'Short-term (weeks-months)',
      longTerm: 'Long-term (years)',
    },
  },

  // --- Route prefix ---
  routePrefix: 'cities',
  projectTypeKey: 'cities_communities',

  // --- Demo data ---
  demos: [
    { id: 'gwcc', label: 'Great Cities Challenge', loader: () => import('../../citiesCommunities/engine/gwccDemo') },
  ],

  // --- Feature flags ---
  hasOptimizer: true,
  hasImpactModel: true,
  hasPopulationEditor: false,
  isOfflineOnly: true,  // No backend API integration yet
};
